import type { ResourceId } from "@@/shared/acquire/resources/resource-id";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import { emptyArrayOf } from "@@/utilities/empty";
import { z } from "zod";
import type { DiscountCondition } from "./discount-condition";
import type {
    DiscountConditionId} from "./discount-condition-id";
import {
    createDiscountConditionId
} from "./discount-condition-id";
import type { DiscountConditionValidator } from "./discount-condition-validator";

const resourceConditionType = "RESOURCE_CONDITION";
type ResourceConditionType = typeof resourceConditionType;

type ResourceCondition = {
    _id: DiscountConditionId;
    type: ResourceConditionType;
    resourceIds: ResourceId[];
};

const resourceConditionFactory = (ids: ResourceId[]): ResourceCondition => {
    return {
        _id: createDiscountConditionId(),
        type: "RESOURCE_CONDITION",
        resourceIds: ids,
    };
};

const getResourceConditionValidator =
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        condition: ResourceCondition,
    ): DiscountConditionValidator<TOrderItem> =>
    (items: TOrderItem[], params: { resource: ResourceId }) => {
        const { resource } = params;

        return condition.resourceIds.some(f => f === resource)
            ? items
            : emptyArrayOf<TOrderItem>();
    };

const resourceConditionZodSchema: z.ZodSchema<ResourceCondition> = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountConditionId>,
    type: z.literal("RESOURCE_CONDITION"),
    resourceIds: z.array(z.string()) as unknown as z.ZodSchema<ResourceId[]>,
});

const isResourceCondition = (
    condition: DiscountCondition,
): condition is ResourceCondition => {
    return condition.type === resourceConditionType;
};

export {
    getResourceConditionValidator,
    isResourceCondition,
    resourceConditionFactory,
    resourceConditionType,
    resourceConditionZodSchema,
};
export type { ResourceCondition, ResourceConditionType };
